/**
 * Side Select Node - Player View
 *
 * Dramatic two-sided display for team side selection.
 * Coin-flip winner picks Attack/Defense with contrasting
 * warm/cool split-screen feel. Non-choosing team watches
 * with a waiting state. Matches the polished node quality bar.
 */

import { getTeamNames } from "@rivaled/shared";
import { AnimatePresence, motion } from "framer-motion";
import { Check, Coins } from "lucide-react";
import { useMemo, useState } from "react";
import { cn } from "../../../lib/utils";
import { useCurrentPlayerId } from "../../hooks/useCurrentPlayerId";
import type { NodePlayerViewProps } from "../types";

type SideOption = {
  id: string;
  name: string;
  description?: string;
};

type SideSelectState = {
  sides: SideOption[];
  teamSides?: Record<number, string>;
  selectingTeam: number;
  coinFlipResult?: number | { winningTeam: number } | null;
};

// Side visual config -- warm/cool contrast
const SIDE_THEMES: Record<
  string,
  {
    gradient: string;
    glow: string;
    icon: "sword" | "shield";
    accent: string;
    accentBg: string;
    accentBorder: string;
  }
> = {
  attack: {
    gradient: "from-red-500/20 via-orange-500/10 to-transparent",
    glow: "bg-red-500/[0.06]",
    icon: "sword",
    accent: "text-red-400",
    accentBg: "bg-red-500/15",
    accentBorder: "border-red-500/30",
  },
  defense: {
    gradient: "from-blue-500/20 via-cyan-500/10 to-transparent",
    glow: "bg-blue-500/[0.06]",
    icon: "shield",
    accent: "text-blue-400",
    accentBg: "bg-blue-500/15",
    accentBorder: "border-blue-500/30",
  },
};

function getSideTheme(side: SideOption, index: number) {
  const key = side.id.toLowerCase();
  if (SIDE_THEMES[key]) return SIDE_THEMES[key];
  // Name-based fallback
  const name = side.name.toLowerCase();
  if (
    name.includes("attack") ||
    name.includes("offense") ||
    name.includes("terrorist") ||
    name.includes("radiant")
  ) {
    return SIDE_THEMES.attack;
  }
  if (name.includes("defen") || name.includes("counter") || name.includes("dire")) {
    return SIDE_THEMES.defense;
  }
  // Alternate warm/cool for unknown sides
  return index % 2 === 0 ? SIDE_THEMES.attack : SIDE_THEMES.defense;
}

function getCoinFlipWinner(coinFlipResult: SideSelectState["coinFlipResult"]): number | null {
  if (coinFlipResult == null) return null;
  if (typeof coinFlipResult === "number") return coinFlipResult;
  return coinFlipResult.winningTeam;
}

// SVG icons for Attack / Defense -- bold filled geometry, sized via className
function SwordIcon({ className }: { className?: string }) {
  return (
    <svg aria-hidden="true" viewBox="0 0 32 32" fill="none" className={className}>
      {/* Blade: filled pentagon tapering to a point */}
      <path
        d="M16 3 L20 8 L20 19 L12 19 L12 8 Z"
        fill="currentColor"
        fillOpacity="0.18"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      {/* Fuller groove down the blade */}
      <path
        d="M16 6 L16 18"
        stroke="currentColor"
        strokeWidth="1"
        strokeOpacity="0.55"
        strokeLinecap="round"
      />
      {/* Crossguard */}
      <path d="M7 19 L25 19" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
      {/* Grip */}
      <path d="M16 19 L16 26" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
      {/* Pommel */}
      <circle cx="16" cy="27.5" r="1.75" fill="currentColor" />
    </svg>
  );
}

function ShieldIcon({ className }: { className?: string }) {
  return (
    <svg aria-hidden="true" viewBox="0 0 32 32" fill="none" className={className}>
      {/* Heater shield: flat top, tapering curved bottom */}
      <path
        d="M16 3 L5 6 V15 C5 21.5 10 26.5 16 29 C22 26.5 27 21.5 27 15 V6 Z"
        fill="currentColor"
        fillOpacity="0.16"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      {/* Heraldic quartering -- vertical */}
      <path d="M16 6 L16 28" stroke="currentColor" strokeWidth="1" strokeOpacity="0.35" />
      {/* Heraldic quartering -- horizontal */}
      <path d="M6 13 L26 13" stroke="currentColor" strokeWidth="1" strokeOpacity="0.35" />
    </svg>
  );
}

export function SideSelectView({
  nodeState,
  players: _players,
  context,
  onAction,
}: NodePlayerViewProps<SideSelectState>) {
  const userId = useCurrentPlayerId();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const state = nodeState.state as SideSelectState;
  const config = nodeState.config as { labels?: { title?: string } };
  const title = config.labels?.title ?? "Side Selection";
  const teams = context.teams as Record<string, number> | undefined;
  const myTeam = teams?.[userId || ""] ?? 0;
  const isSelectingTeam = myTeam === state.selectingTeam;
  const hasSelected = !!state.teamSides && Object.keys(state.teamSides).length > 0;
  const coinFlipWinner = getCoinFlipWinner(state.coinFlipResult);

  // Seeded team names
  const teamNames = useMemo(
    () => getTeamNames(context.matchId, context.teamCount),
    [context.matchId, context.teamCount],
  );
  const getTeamName = (teamIndex: number) => teamNames[teamIndex] ?? `Team ${teamIndex}`;

  const handleSelectSide = async (sideId: string) => {
    if (!isSelectingTeam || hasSelected) return;

    setIsSubmitting(true);
    setError(null);

    const result = await onAction("select_side", { sideId, userId });

    if (!result.success && result.error) {
      setError(result.error);
    }

    setIsSubmitting(false);
  };

  return (
    <div className="@container relative overflow-hidden rounded-xl bg-surface-900 border border-surface-700/50">
      {/* Atmospheric background -- dual-tone glow */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 w-1/2 h-full bg-gradient-to-br from-red-500/[0.03] to-transparent" />
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-bl from-blue-500/[0.03] to-transparent" />
        <div className="absolute inset-0 noise-overlay" />
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-red-500/20 via-transparent to-blue-500/20" />
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-red-500/10 via-transparent to-blue-500/10" />
      </div>

      <div className="relative z-10 px-4 @md:px-8 @3xl:px-10 py-5 @md:py-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="text-center mb-6"
        >
          {title && (
            <h3 className="font-display text-sm @md:text-base font-bold uppercase tracking-[0.3em] text-brand-400 mb-2">
              {title}
            </h3>
          )}

          {/* Coin flip result */}
          {coinFlipWinner != null && !hasSelected && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2, duration: 0.4 }}
              className="mb-3"
            >
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-surface-800/80 border border-surface-700/50">
                <Coins size={14} className="text-amber-400" aria-hidden="true" />
                <span className="text-xs text-text-secondary">
                  Coin flip won by{" "}
                  <span className="font-display font-semibold text-text-primary">
                    {getTeamName(coinFlipWinner)}
                  </span>
                </span>
              </div>
            </motion.div>
          )}

          {/* Status message */}
          <AnimatePresence mode="wait">
            {hasSelected ? (
              <motion.p
                key="done"
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                className="text-sm @sm:text-base text-brand-400 font-semibold"
              >
                Sides locked in
              </motion.p>
            ) : isSelectingTeam ? (
              <motion.p
                key="your-pick"
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                className="text-sm @sm:text-base text-brand-400 font-semibold"
              >
                Your team picks a side
              </motion.p>
            ) : (
              <motion.p
                key="waiting"
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                className="text-sm @sm:text-base text-text-secondary"
              >
                {getTeamName(state.selectingTeam)} is choosing a side...
              </motion.p>
            )}
          </AnimatePresence>
        </motion.div>

        {/* Error */}
        <AnimatePresence>
          {error && (
            <motion.p
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="text-error-400 text-sm text-center mb-4"
            >
              {error}
            </motion.p>
          )}
        </AnimatePresence>

        {/* Side selection -- choosing state */}
        {!hasSelected && (
          <div
            className={cn(
              "grid gap-4 @md:gap-5 max-w-3xl mx-auto",
              state.sides.length === 2
                ? "grid-cols-1 @md:grid-cols-2"
                : state.sides.length === 3
                  ? "grid-cols-1 @lg:grid-cols-3"
                  : "grid-cols-1 @md:grid-cols-2",
            )}
          >
            {state.sides.map((side, index) => (
              <SideCard
                key={side.id}
                side={side}
                theme={getSideTheme(side, index)}
                isSelectingTeam={isSelectingTeam}
                disabled={isSubmitting || !isSelectingTeam}
                index={index}
                onSelect={handleSelectSide}
              />
            ))}
          </div>
        )}

        {/* Side results -- after selection */}
        <AnimatePresence>
          {hasSelected && state.teamSides && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="space-y-4 max-w-2xl mx-auto"
            >
              {Object.entries(state.teamSides).map(([teamStr, sideId]) => {
                const teamNum = Number(teamStr);
                const side = state.sides.find((s) => s.id === sideId);
                const theme = side
                  ? getSideTheme(side, state.sides.indexOf(side))
                  : SIDE_THEMES.attack;
                const isMyTeam = teamNum === myTeam;
                const Icon = theme.icon === "sword" ? SwordIcon : ShieldIcon;

                return (
                  <motion.div
                    key={teamStr}
                    initial={{ opacity: 0, x: isMyTeam ? -20 : 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, delay: isMyTeam ? 0.1 : 0.25 }}
                    className={cn(
                      "relative flex items-center gap-4 @md:gap-5 px-5 @md:px-6 py-4 @md:py-5 rounded-lg overflow-hidden",
                      "border transition-all",
                      isMyTeam
                        ? `${theme.accentBorder} ${theme.glow}`
                        : "border-surface-700/40 bg-surface-800/40",
                    )}
                  >
                    {/* Side gradient accent */}
                    <div
                      className={cn(
                        "absolute inset-0 bg-gradient-to-r pointer-events-none opacity-40",
                        theme.gradient,
                      )}
                    />

                    {/* Side icon */}
                    <div
                      className={cn(
                        "relative shrink-0 w-12 h-12 rounded-lg flex items-center justify-center",
                        theme.accentBg,
                      )}
                    >
                      <Icon className={cn(theme.accent, "w-9 h-9")} />
                    </div>

                    {/* Team + side info */}
                    <div className="relative flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span
                          className={cn(
                            "font-display text-sm font-semibold uppercase tracking-wider",
                            isMyTeam ? "text-text-primary" : "text-text-secondary",
                          )}
                        >
                          {getTeamName(teamNum)}
                        </span>
                        {isMyTeam && (
                          <span className="text-[10px] uppercase tracking-wider text-brand-400 font-medium">
                            (you)
                          </span>
                        )}
                      </div>
                      <p className={cn("font-display font-bold text-lg", theme.accent)}>
                        {side?.name ?? sideId}
                      </p>
                      {side?.description && (
                        <p className="text-xs text-text-secondary mt-0.5">{side.description}</p>
                      )}
                    </div>

                    {/* Checkmark */}
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: "spring", stiffness: 400, damping: 20, delay: 0.4 }}
                      className={cn(
                        "relative shrink-0 w-8 h-8 rounded-full flex items-center justify-center",
                        theme.accentBg,
                      )}
                    >
                      <Check size={14} className={theme.accent} aria-hidden="true" />
                    </motion.div>
                  </motion.div>
                );
              })}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Waiting indicator for non-selecting team */}
        {!hasSelected && !isSelectingTeam && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="mt-8 text-center"
          >
            <div className="inline-flex items-center gap-3 px-4 py-2.5 rounded-lg bg-surface-800/60 border border-surface-700/40">
              <div className="w-4 h-4 rounded-full border-2 border-surface-500/50 border-t-surface-300/80 animate-spin" />
              <span className="text-sm text-text-secondary">
                Waiting for {getTeamName(state.selectingTeam)}...
              </span>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Side card component -- dramatic split-screen choice buttons
// ---------------------------------------------------------------------------

type SideCardProps = {
  side: SideOption;
  theme: ReturnType<typeof getSideTheme>;
  isSelectingTeam: boolean;
  disabled: boolean;
  index: number;
  onSelect: (sideId: string) => void;
};

function SideCard({ side, theme, isSelectingTeam, disabled, index, onSelect }: SideCardProps) {
  const delay = 0.15 + index * 0.1;
  const Icon = theme.icon === "sword" ? SwordIcon : ShieldIcon;

  return (
    <motion.button
      initial={{ opacity: 0, y: 30, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.5, delay, type: "spring", stiffness: 200, damping: 20 }}
      whileHover={!disabled ? { scale: 1.03, y: -2 } : undefined}
      whileTap={!disabled ? { scale: 0.97 } : undefined}
      onClick={() => onSelect(side.id)}
      disabled={disabled}
      className={cn(
        "group relative rounded-lg overflow-hidden text-center",
        "transition-all duration-200",
        "focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-surface-900",
        theme.icon === "sword" ? "focus:ring-red-400" : "focus:ring-blue-400",
        isSelectingTeam
          ? "cursor-pointer ring-1 ring-surface-700/50 hover:ring-2"
          : "cursor-not-allowed opacity-60 ring-1 ring-surface-700/30",
        isSelectingTeam && theme.icon === "sword" && "hover:ring-red-500/50",
        isSelectingTeam && theme.icon === "shield" && "hover:ring-blue-500/50",
      )}
    >
      {/* Background with gradient */}
      <div className={cn("relative px-6 py-5 @md:py-6", "bg-surface-800/70")}>
        {/* Directional gradient accent */}
        <div
          className={cn(
            "absolute inset-0 bg-gradient-to-b pointer-events-none opacity-60",
            theme.gradient,
          )}
        />

        {/* Decorative corner lines */}
        <div
          className={cn(
            "absolute top-0 left-0 w-10 h-px transition-opacity duration-300",
            theme.accent.replace("text-", "bg-").replace("400", "500/40"),
            isSelectingTeam ? "opacity-100" : "opacity-30",
          )}
        />
        <div
          className={cn(
            "absolute top-0 left-0 h-10 w-px transition-opacity duration-300",
            theme.accent.replace("text-", "bg-").replace("400", "500/40"),
            isSelectingTeam ? "opacity-100" : "opacity-30",
          )}
        />
        <div
          className={cn(
            "absolute bottom-0 right-0 w-10 h-px transition-opacity duration-300",
            theme.accent.replace("text-", "bg-").replace("400", "500/40"),
            isSelectingTeam ? "opacity-100" : "opacity-30",
          )}
        />
        <div
          className={cn(
            "absolute bottom-0 right-0 h-10 w-px transition-opacity duration-300",
            theme.accent.replace("text-", "bg-").replace("400", "500/40"),
            isSelectingTeam ? "opacity-100" : "opacity-30",
          )}
        />

        {/* Large side icon */}
        <motion.div
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: delay + 0.15, type: "spring", stiffness: 300, damping: 25 }}
          className="relative flex justify-center mb-4"
        >
          <div
            className={cn(
              "w-20 h-20 rounded-xl flex items-center justify-center transition-all duration-200",
              theme.accentBg,
              isSelectingTeam && "group-hover:scale-110",
            )}
          >
            <Icon className={cn(theme.accent, "w-12 h-12 transition-colors duration-200")} />
          </div>
        </motion.div>

        {/* Side name */}
        <p
          className={cn(
            "relative font-display font-bold text-xl @sm:text-2xl uppercase tracking-wider transition-colors duration-200",
            isSelectingTeam ? theme.accent : "text-surface-400",
          )}
        >
          {side.name}
        </p>

        {/* Description */}
        {side.description && (
          <p className="relative text-xs text-text-secondary mt-2 leading-relaxed max-w-[200px] mx-auto">
            {side.description}
          </p>
        )}

        {/* Hover prompt */}
        {isSelectingTeam && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: delay + 0.3 }}
            className="relative mt-4"
          >
            <span
              className={cn(
                "inline-block text-[10px] uppercase tracking-widest font-medium transition-colors duration-200",
                "text-surface-500 group-hover:text-surface-300",
              )}
            >
              Click to select
            </span>
          </motion.div>
        )}

        {/* Top glow line on hover */}
        {isSelectingTeam && (
          <div
            className={cn(
              "absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300",
              theme.icon === "sword" ? "via-red-500/50" : "via-blue-500/50",
            )}
          />
        )}
      </div>
    </motion.button>
  );
}
